/**
 * TEMP-H-001: IterationsList Component
 * 
 * Displays stage iterations with accepted markers and artifact links.
 * Shows newest iterations first with metadata like cost and creation time.
 */

import React from 'react';
import { motion } from 'framer-motion';
import { Clock, DollarSign, CheckCircle, FileText, ExternalLink } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import type { StageType } from '@/types/execution';
import type { IterationItem } from '@/lib/api-types';
import { useIterations } from '@/hooks/useIterations';
import { cn } from '@/utils/cn';

interface IterationsListProps {
  executionId: string;
  stage: StageType;
  className?: string;
}

interface IterationCardProps {
  iteration: IterationItem;
  isAccepted: boolean;
}

const IterationCard: React.FC<IterationCardProps> = ({ iteration, isAccepted }) => {
  const formattedTime = formatDistanceToNow(new Date(iteration.createdAt), { addSuffix: true });

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className={cn(
        'bg-slate-800/50 border rounded-lg p-4 backdrop-blur-sm',
        isAccepted 
          ? 'border-emerald-500/50 bg-emerald-900/20' 
          : 'border-slate-700/50 hover:border-slate-600/50'
      )}
    >
      {/* Header with iteration number and accepted badge */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-slate-300">
            Iteration #{iteration.iterationNumber}
          </span>
          {isAccepted && (
            <div className="flex items-center gap-1 bg-emerald-500/20 text-emerald-400 px-2 py-1 rounded-full text-xs font-medium">
              <CheckCircle className="w-3 h-3" />
              Accepted
            </div>
          )}
        </div>
        
        {/* Metadata */}
        <div className="flex items-center gap-3 text-xs text-slate-400">
          <div className="flex items-center gap-1">
            <Clock className="w-3 h-3" />
            {formattedTime}
          </div>
          {iteration.costUsd && (
            <div className="flex items-center gap-1">
              <DollarSign className="w-3 h-3" />
              ${iteration.costUsd.toFixed(3)}
            </div>
          )}
        </div>
      </div>

      {/* Model info */}
      {iteration.modelUsed && (
        <div className="text-xs text-slate-500 mb-3">
          Generated with {iteration.modelUsed}
        </div>
      )}

      {/* Artifacts */}
      {iteration.artifacts.length > 0 && (
        <div className="space-y-2">
          <div className="text-xs font-medium text-slate-400">Artifacts:</div>
          <div className="space-y-1">
            {iteration.artifacts.map((artifact, index) => (
              <a
                key={index}
                href={artifact.uri}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 text-sm text-blue-400 hover:text-blue-300 transition-colors"
              >
                <FileText className="w-3 h-3" />
                {artifact.name}
                <ExternalLink className="w-3 h-3" />
              </a>
            ))}
          </div>
        </div>
      )}
    </motion.div>
  );
};

const IterationsList: React.FC<IterationsListProps> = ({ 
  executionId, 
  stage, 
  className 
}) => {
  const { data: iterations, isLoading, error } = useIterations(executionId, stage);

  if (isLoading) {
    return (
      <div className={cn('space-y-3', className)}>
        {[1, 2, 3].map((i) => (
          <div key={i} className="bg-slate-800/30 border border-slate-700/30 rounded-lg p-4 animate-pulse">
            <div className="h-4 bg-slate-700/50 rounded mb-2 w-24"></div>
            <div className="h-3 bg-slate-700/30 rounded w-32"></div>
          </div>
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div className={cn('text-center py-8 text-slate-400', className)}>
        <div className="text-sm">Failed to load iterations</div>
        <div className="text-xs mt-1">{error.message}</div>
      </div>
    );
  }

  if (!iterations || iterations.items.length === 0) {
    return (
      <div className={cn('text-center py-8 text-slate-400', className)}>
        <div className="text-sm">No iterations yet</div>
        <div className="text-xs mt-1">Iterations will appear here as the stage progresses</div>
      </div>
    );
  }

  return (
    <div className={cn('space-y-3', className)}>
      {iterations.items.map((iteration) => (
        <IterationCard
          key={iteration.iterationNumber}
          iteration={iteration}
          isAccepted={iteration.accepted}
        />
      ))}
    </div>
  );
};

export default IterationsList;